import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, BehaviorSubject, throwError } from 'rxjs';
import { map, catchError, tap, retry } from 'rxjs/operators';
import {TeacherApiResponse, TeacherResult, Worker} from "./worker.models";
import {SystemApp} from "../../constans/SystemConfigurations";
import {ApiUrl} from "../../shared/models/apiurl.models";


@Injectable({
    providedIn: 'root',
})
export class WorkersService {
    private options = {
        headers: new HttpHeaders({
            'Content-Type': 'application/json',
            'Authorization': this.apiKey.getJWTToken()
        })
    }
    // ── Configuration ──
    // Update these to match your environment
    private readonly API_BASE = SystemApp.base_url_api;
    private readonly ENDPOINT = '/clientAjax/productVFilteriewSet';
    private readonly AUTH_TOKEN = 'Token 9813dbb24b3f663de0f5b241ff20022c608ef05f';
    private readonly DEFAULT_LIMIT = 36;
    private readonly DEFAULT_PLACEHOLDER =
        'https://ui-avatars.com/api/?background=1b4f8a&color=fff&size=300&font-size=0.4&name=';

    // ── State ──
    private workersSubject = new BehaviorSubject<Worker[]>([]);
    private loadingSubject = new BehaviorSubject<boolean>(false);
    private errorSubject = new BehaviorSubject<string | null>(null);
    private totalCountSubject = new BehaviorSubject<number>(0);

    public workers$ = this.workersSubject.asObservable();
    public loading$ = this.loadingSubject.asObservable();
    public error$ = this.errorSubject.asObservable();
    public totalCount$ = this.totalCountSubject.asObservable();

    constructor(private http: HttpClient, private apiKey: ApiUrl) {}

    // ── Headers ──
    private getHeaders(): HttpHeaders {
        return new HttpHeaders({
            'Content-Type': 'application/json',
            Accept: 'application/json, text/plain, */*',
            Authorization: this.AUTH_TOKEN,
        });
    }

    // ──────────────────────────────────────────────
    // FETCH professionals/workers. The backend still stores these mixed
    // professional profiles under its legacy `teacher` resource type; the
    // public UI intentionally presents them as general workers.
    // ──────────────────────────────────────────────
    fetchWorkers(
        limit: number = this.DEFAULT_LIMIT,
        offset: number = 1
    ): Observable<Worker[]> {
        this.loadingSubject.next(true);
        this.errorSubject.next(null);

        const params = new HttpParams()
            .set('limit', limit.toString())
            .set('offset', offset.toString())
            .set('model_class', 'teacher');

        const url = `${this.API_BASE}${this.ENDPOINT}`;

        return this.http
            .post<TeacherApiResponse>(url, {}, { headers: this.options.headers,params: params })
            .pipe(
                retry(2), // retry twice on failure
                map((response) => {
                    this.totalCountSubject.next(response.page_size || 0);
                    const workers = (response.object_list || []).map((item) =>
                        this.mapToWorker(item)
                    );
                    return workers;
                }),
                tap((workers) => {
                    this.workersSubject.next(workers);
                    this.loadingSubject.next(false);
                }),
                catchError((err) => {
                    const message =
                        err.status === 0
                            ? 'Cannot reach server — check your network or API URL'
                            : err.status === 401
                                ? 'Authentication failed — check your token'
                                : err.status === 404
                                    ? 'Endpoint not found — check the API path'
                                    : `Error ${err.status}: ${err.message}`;

                    this.errorSubject.next(message);
                    this.loadingSubject.next(false);
                    console.error('[WorkersService] Fetch failed:', err);
                    return throwError(() => new Error(message));
                })
            );
    }

    // ──────────────────────────────────────────────
    //  FETCH ALL — Auto-paginate through all pages
    // ──────────────────────────────────────────────
    async fetchAllWorkers(pageSize: number = 36): Promise<Worker[]> {
        this.loadingSubject.next(true);
        this.errorSubject.next(null);

        const allWorkers: Worker[] = [];
        let offset = 1;
        let hasMore = true;

        try {
            while (hasMore) {
                const params = new HttpParams()
                    .set('limit', pageSize.toString())
                    .set('offset', offset.toString())
                    .set('model_class', 'teacher');

                const url = `${this.API_BASE}${this.ENDPOINT}`;
                const response = await this.http
                    .post<TeacherApiResponse>(url, {}, {
                        headers: this.getHeaders(),
                        params,
                    })
                    .toPromise();

                if (response && response.object_list) {
                    const mapped = response.object_list.map((item) =>
                        this.mapToWorker(item)
                    );
                    allWorkers.push(...mapped);
                    this.totalCountSubject.next(response.page_size || allWorkers.length);

                    // Check if there are more pages
                    hasMore = response.next !== null && mapped.length === pageSize;
                    offset += pageSize;
                } else {
                    hasMore = false;
                }
            }

            this.workersSubject.next(allWorkers);
            this.loadingSubject.next(false);
            return allWorkers;
        } catch (err: any) {
            this.errorSubject.next(err.message || 'Failed to fetch all workers');
            this.loadingSubject.next(false);
            throw err;
        }
    }

    // ──────────────────────────────────────────────
    //  MAP — Convert API response to Worker model
    // ──────────────────────────────────────────────
    //  Adjust field mappings here to match your actual
    //  API response structure.
    // ──────────────────────────────────────────────
    private mapToWorker(item: TeacherResult): Worker {
        // Name: try multiple possible field names
        const name =
            item.full_name ||
            item.name ||
            [item.first_name, item.last_name].filter(Boolean).join(' ') ||
            item.title ||
            `Worker #${item.id}`;

        // Role / profession
        const role =
            item.role ||
            item.profession ||
            item.specialty ||
            item.category ||
            '';

        // Rating: normalize to 0–5
        let rating = item.avg_rating ?? item.rating ?? 0;
        if (rating > 5) rating = 5;
        rating = Math.round(rating * 10) / 10;

        // Image: try multiple possible field names
        const rawImage =
            item.image ||
            item.photo ||
            item.avatar ||
            item.profile_image ||
            null;

        let imageUrl: string;
        if (rawImage) {
            // Handle relative URLs
            imageUrl = rawImage.startsWith('http')
                ? rawImage
                : `${this.API_BASE}${rawImage.startsWith('/') ? '' : '/'}${rawImage}`;
        } else {
            // Generate placeholder from name
            imageUrl = `${this.DEFAULT_PLACEHOLDER}${encodeURIComponent(name)}`;
        }

        // City: usually a flat string (item.address), but internal (admin) ads
        // merged into this listing carry Product's `city` as a nested
        // {nameHe, ...} object instead — unwrap it so it doesn't render as
        // "[object Object]".
        const rawCity: any = item.city ?? item.address;
        const city = rawCity && typeof rawCity === 'object' ? rawCity.nameHe : rawCity;

        return {
            id: item?.id,
            name,
            role,
            rating,
            imageUrl,
            phone: item.phone,
            email: item.email,
            description: item.description || item.bio,
            city,
            isActive: item.is_active ?? true,
        };
    }
}
